import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSession } from "../../auth/session"
import { useSelector } from "react-redux"
import Api from "../../api/api"
import { meSelector } from "../../redux/globalSlice"
import { Claim, ClaimType, Organization, Sku, UserRole } from "../../api/models"
import Spinner from "../widgets/Spinner"
import AddSkuDialog from "../AddSkuDialog"
import OrgChip, { orgLogoIcon } from "../widgets/OrgChip"
import CardImage from "../widgets/CardImage"
import SkuName from "../widgets/SkuName"
import CopyableId from "../widgets/CopyableId"
import ChipSelect from "../widgets/ChipSelect"
import { bundleIcon, freeIcon, notShippedIcon, oneTimeIcon, rewardIcon, shippingIcon, singleIcon, subscriptionIcon } from "../widgets/pillIcons"
import { buildImageSrcSet } from "../widgets/imageSrcSet"

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 300
const SEARCH_MIN_CHARS = 2

// bundle = several license claims, single = exactly one
type BundleFilter = "bundle" | "single"

const bundleFilterLabels: Record<BundleFilter, string> = {
  bundle: "Bundles",
  single: "Single product",
}

// One dropdown over the sku's offerings: the offering paymentType enum, plus
// "shipping" (any offering at all) and "not_shipped" (none) as aggregates
type OfferingFilter = "shipping" | "one_time" | "subscription" | "free" | "reward" | "not_shipped"

const offeringFilterLabels: Record<OfferingFilter, string> = {
  shipping: "Shipping",
  one_time: "One time",
  subscription: "Subscriptions",
  free: "Free",
  reward: "Rewards",
  not_shipped: "Not shipped",
}

const offeringBadges: Record<"one_time" | "subscription" | "free" | "reward" | "not_shipped", { label: string, icon: ReactNode }> = {
  one_time: { label: "One time", icon: oneTimeIcon },
  subscription: { label: "Subscription", icon: subscriptionIcon },
  free: { label: "Free", icon: freeIcon },
  reward: { label: "Reward", icon: rewardIcon },
  not_shipped: { label: "Not shipped", icon: notShippedIcon },
}

// One badge per distinct offering kind; a sku with no offerings is not shipped
function skuOfferingBadgeKeys(sku: Sku): ("one_time" | "subscription" | "free" | "reward" | "not_shipped")[] {
  if (sku.offerings.length === 0) return ["not_shipped"]
  const kinds = new Set(sku.offerings.map(o => o.paymentType))
  return (["one_time", "subscription", "free", "reward"] as const).filter(k => kinds.has(k))
}

// One badge per filter dimension, showing the side of the pair the SKU is on;
// a dimension that doesn't apply (no price set, no license claims) shows none.
function SkuRow({ sku, isBundle, isSingle }: { sku: Sku, isBundle: boolean, isSingle: boolean }) {
  const navigate = useNavigate()
  const imageUrl = sku.imageUrl
  const imageSrcSet = imageUrl ? buildImageSrcSet(imageUrl, sku.availableWidths) : undefined

  return (
    <div
      className="license-group license-group-with-img"
      style={{ cursor: "pointer" }}
      onClick={() => navigate(`/skus/${sku.id}`)}
    >
      <div className="license-group-img-container">
        <CardImage imageUrl={imageUrl} availableWidths={sku.availableWidths} alt={sku.name} />
      </div>
      {/* pills flow left right after the name & version (the class's
          space-between is overridden); the org chip's own margin-left:auto
          sends it to the right edge */}
      <div className="product-group-header" style={{ justifyContent: "flex-start", flexWrap: "wrap", rowGap: 4 }}>
        {imageUrl && (
          <img src={imageUrl} srcSet={imageSrcSet} sizes="44px" alt="" className="product-header-icon" />
        )}
        <h2 className="section-title sku-row-title">
          <SkuName sku={sku} variant="section" />
          {sku.latestVersion != null && (
            <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt", fontWeight: "normal", marginLeft: 8 }}>
              v{sku.latestVersion}
            </span>
          )}
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {skuOfferingBadgeKeys(sku).map(kind => (
            <span key={kind} className="badge">
              {offeringBadges[kind].icon}
              {offeringBadges[kind].label}
            </span>
          ))}
          {(isBundle || isSingle) && (
            <span className="badge">
              {isBundle ? bundleIcon : singleIcon}
              {isBundle ? "Bundle" : "Single product"}
            </span>
          )}
        </div>
        <OrgChip orgId={sku.ownerId} />
        {/* the id breaks onto its own full-width line under the name row */}
        <div style={{ flexBasis: "100%" }}>
          <CopyableId id={sku.id} />
        </div>
      </div>
      {sku.description && (
        <p style={{
          margin: 0,
          color: "var(--nfa-light-grey)",
          fontSize: "10pt",
          gridArea: "list",
          padding: "0 20px 20px"
        }}>
          {sku.description}
        </p>
      )}
    </div>
  )
}

// Every criterion is server-side on GET /admin/skus: ?search= (name,
// description, highlights; ≥2 chars, debounced), ?sellerId=, ?paymentType=
// (the offerings filter incl. the "shipping" aggregate — the wire name is the
// shipped production contract) and ?shape= (bundle/single), all cursor-
// paginated together. Local state like Products/Users — see the note in
// SkuEntitlements about the singleton redux pagination helpers.
export default function Skus() {
  const session = useSession()
  const navigate = useNavigate()
  const accessToken = session.accessToken ?? ""
  const [addSkuOpen, setAddSkuOpen] = useState(false)

  // the sellerId filter is admin-only on the backend (a Developer's listing is
  // already scoped to their own SKUs)
  const isAdmin = useSelector(meSelector)?.data?.role === UserRole.Admin

  const [searchText, setSearchText] = useState("")
  // "" = every organization (the owner filter is admin-only server-side)
  const [ownerFilter, setOwnerFilter] = useState("")
  const [organizations, setOrganizations] = useState<Organization[]>([])
  useEffect(() => {
    if (!isAdmin || !accessToken) return
    let stale = false
    Api.listOrganizations({ accessToken })
      .then(({ organizations }) => { if (!stale) setOrganizations(organizations) })
      .catch(() => { })
    return () => { stale = true }
  }, [isAdmin, accessToken])
  const [bundleFilter, setBundleFilter] = useState<"" | BundleFilter>("")
  const [offeringFilter, setOfferingFilter] = useState<"" | OfferingFilter>("")
  const [skus, setSkus] = useState<Sku[] | undefined>(undefined)
  const [claims, setClaims] = useState<Claim[]>([])
  const [link, setLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  // stale responses (criteria changed mid-flight) must not land in state
  const requestSeq = useRef(0)

  const search = searchText.trim().length >= SEARCH_MIN_CHARS ? searchText.trim() : undefined
  const sellerId = ownerFilter || undefined

  // cursor === null loads the first page (replacing state), a cursor appends
  const loadPage = useCallback(async (cursor: string | null) => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(false)
    try {
      const page = await Api.getSkus({
        accessToken, search, sellerId,
        paymentType: offeringFilter || undefined,
        shape: bundleFilter || undefined,
        link: cursor ?? undefined, count: PAGE_SIZE
      })
      if (seq !== requestSeq.current) return
      setSkus((prev) => cursor === null ? page.skus : [...(prev ?? []), ...page.skus])
      setClaims((prev) => cursor === null ? page.claims : [...prev, ...page.claims])
      setLink(page.link)
    } catch {
      if (seq === requestSeq.current) setError(true)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [accessToken, search, sellerId, offeringFilter, bundleFilter])

  useEffect(() => {
    requestSeq.current++    // discard any in-flight page of the old criteria
    setSkus(undefined)
    setLink(null)
    // debounce keystrokes only; the unfiltered listing loads immediately
    const timer = setTimeout(() => loadPage(null), search !== undefined ? SEARCH_DEBOUNCE_MS : 0)
    return () => clearTimeout(timer)
  }, [loadPage, search])

  const { bundleSkuIds, singleLicenseSkuIds } = useMemo(() => {
    const licenseCounts = new Map<string, number>()
    for (const claim of claims) {
      if (claim.type === ClaimType.License)
        licenseCounts.set(claim.skuId, (licenseCounts.get(claim.skuId) ?? 0) + 1)
    }
    const bundles = new Set<string>()
    const singles = new Set<string>()
    licenseCounts.forEach((n, skuId) => {
      if (n > 1) bundles.add(skuId)
      else if (n === 1) singles.add(skuId)
    })
    return { bundleSkuIds: bundles, singleLicenseSkuIds: singles }
  }, [claims])

  const isLoading = skus === undefined && !error
  const filtersActive = search !== undefined || ownerFilter !== "" || offeringFilter !== "" || bundleFilter !== ""

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h1 className="page-title" style={{ margin: 0 }}>SKUs</h1>
        <button
          className="card-link"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
          onClick={() => setAddSkuOpen(true)}
        >
          Add SKU
        </button>
      </div>

      <div className="filter-bar">
        <input
          className="filter-input"
          type="text"
          placeholder="name or description…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        {isAdmin && (
          <ChipSelect
            value={ownerFilter}
            onChange={setOwnerFilter}
            options={organizations.map((org) => org.id)}
            labels={Object.fromEntries(organizations.map((org) => [org.id, org.name]))}
            icons={Object.fromEntries(organizations.map((org) => [org.id, orgLogoIcon(org)]))}
            placeholder="All organizations"
          />
        )}
        <ChipSelect
          value={offeringFilter}
          onChange={setOfferingFilter}
          options={["shipping", "one_time", "subscription", "free", "reward", "not_shipped"] as OfferingFilter[]}
          labels={offeringFilterLabels}
          icons={{ shipping: shippingIcon, one_time: oneTimeIcon, subscription: subscriptionIcon, free: freeIcon, reward: rewardIcon, not_shipped: notShippedIcon }}
          placeholder="All offerings"
        />
        <ChipSelect
          value={bundleFilter}
          onChange={setBundleFilter}
          options={["bundle", "single"] as BundleFilter[]}
          labels={bundleFilterLabels}
          icons={{ bundle: bundleIcon, single: singleIcon }}
          placeholder="Bundles & singles"
        />
      </div>

      {error && skus === undefined && (
        <p className="empty-state">Error loading SKUs</p>
      )}

      {isLoading && (
        <p className="empty-state"><Spinner size={16} /> Loading SKUs…</p>
      )}

      {!isLoading && !error && skus!.length === 0 && (
        filtersActive ? (
          <p className="empty-state">No matching SKUs</p>
        ) : (
          <div className="empty-state-block">
            <span>No SKUs yet</span>
            <span className="empty-state-hint">Use the "Add SKU" button to create your first SKU</span>
          </div>
        )
      )}

      {(skus ?? []).map((it) => (
        <SkuRow
          key={it.id}
          sku={it}
          isBundle={bundleSkuIds.has(it.id)}
          isSingle={singleLicenseSkuIds.has(it.id)}
        />
      ))}

      {link !== null && skus !== undefined && (
        loading ? <Spinner size={16} /> : (
          <button className="card-link" onClick={() => loadPage(link)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            Load more
          </button>
        )
      )}
      {error && skus !== undefined && (
        <p className="empty-state">Error loading more SKUs</p>
      )}

      {addSkuOpen && (
        <AddSkuDialog
          accessToken={accessToken}
          isAdmin={isAdmin}
          onClose={() => setAddSkuOpen(false)}
          onCreated={(sku) => navigate(`/skus/${sku.id}`)}
        />
      )}
    </div>
  )
}
